import { useEffect, useRef, useState } from 'react';
import {
  BaseellaStatus,
  InternalEnumTabs,
  TransactionTypeEnum,
} from '../types';
import { PermissionEnum } from '../../../base-types';
import IconAttention from '../../icons/attention';
import ButtonPrimary from '../../button/button-primary';
import ButtonSecondary from '../../button/button-secondary';
import formatDate from '../../../helpers/format-date';
import TransactionStatusChange from '../incoming-outgoing-transaction-status-change';
import Tabs from '../../tabs/tabs';
import TransactionWarnings from '../warnings/transation-warning';
import TransactionDocuments from '../documents/documents';
import IncomingOutgoingAmlContent from '../incoming-outgoing-transaction-aml';
import TransactionLogsContent from '../transaction-logs-content';
import ChangeLogs from '../../change-logs/change-logs';
import TransactionSignatures from '../signatures/signatures';
import TransactionAccountingEntries from '../accounting-entries/accounting-entries';
import TransactionChats from '../chats/chats';
import InternalTransactionGeneral from './internal-transaction-general';
import TransactionComments from '../comments/comments';
import { InternalTransactionSidepanelContentFormTypes } from './types';

import styles from '../transaction.module.css';

export default function InternalTransactionSidepanelContentForm({
  tabs,
  setActiveTab,
  activeTab,
  warnings,
  duplicate,
  getTransactionDocuments,
  getGeneralLedgerTransactionValues,
  getTransactionsChats,
  activePermission,
  transactionInfo,
  getSignaturesTransactionById,
  getInternalTransactionByIdAml,
  getLogsTransactionById,
  baseellaStatus,
  canSignTransaction,
  createdByOrganizationId,
  sendCodeInProgress,
  sendCode,
  setRejectPopupVisible,
  statusesInfo,
  onNextStatus,
  updateStatusIsLoading,
  isLoadingStatuses,
  latestQueuedStatus,
  parentScroll,
  parentRef,
  isCreateForm,
  employee,
  onApplyTransactionAction,
  transactionsTypes,
  setBlacklistDataVisible,
  setBlacklistData,
  setBlacklistItemVisible,
  setBlacklistDataItem,
  applyTransactionsWarningIsLoading,
  chats,
  createChatWithMessage,
  chatCreateInProgress,
  isLoadingAml,
  amlInfo,
  senderCompanyOfficials,
  recipientCompanyOfficials,
  isLoadingLogs,
  logs,
  changeLogs,
  documents,
  setSubPanelVisible,
  setSelectedDocumentName,
  setTransactionSidepanelVisible,
  onUploadDocument,
  uploadDocumentInProgress,
  uploadDocumentIsSuccess,
  uploadDocumentErrors,
  selectedDocumentId,
  setSelectedDocumentId,
  downloadInProgress,
  onDownloadDocument,
  getTransactionDocument,
  documentsIsLoading,
  signatures,
  isLoadingSignatures,
  glTransactionsIsLoading,
  glTransactions,
  setGlTransactionSidePanelVisible,
  onGlTransactionClick,
  currenciesList,
  onCreateTransactionChat,
  currentEmployee,
  transactionChatsIsLoading,
  control,
  errors,
  isEditable,
  senderAccounts,
  recipientAccounts,
  senderAccountsIsLoading,
  recipientAccountsIsLoading,
  onSearchAutocomplete,
  fields,
  senderAccountBalance,
  recipientAccountBalance,
  currencyId,
  senderAccountBalances,
  recipientAccountBalances,
  senderAccountBalancesIsLoading,
  recipientAccountBalancesIsLoading,
  overdraftEnabled,
  recipientAccountsWithInfo,
  senderAccountsWithInfo,
  availableCurrencies,
  amount,
  setAmount,
  allCurrenciesList,
  feeText,
  updateSenderBalance,
  updateRecipientBalance,
  getTransactionChangeLogs,
  changeLogsIsLoading,
  users,
  transactionChangeLogsPagination,
  changeLogsIsFetching,
  transactionId,
  onResetTransactionWarningAction,
  resetTransactionsWarningIsLoading,
  resetTransactionsWarningIsSuccess,
  documentsPagination,
  documentsIsFetching,
  transferDate,
  attachedDocumentsErrors,
  uploadedDocumentsErrors,
  transactionComments,
  transactionCommentsPagination,
  getTransactionCommentsAction,
  commentsIsLoading,
  commentsIsFetching,
  onAddCommentToTransaction,
  addCommentInProgress,
  addCommentIsSuccess,
  onEditCommentInTransaction,
  editTransactionCommentInProgress,
  editTrancastionCommentIsSuccess,
  onDeleteCommentInTransaction,
  deleteCommentInTransactionInProgress,
  deleteCommentInTranscationIsSuccess,
  getInternalTransactionWarningById,
  isWarningByIdFetching,
  isWarningByIdLoading,
  warningsInfo,
  selectedWarningId,
  warningActionError,
  clearErrors,
  withTime,
  setWithTime,
}: InternalTransactionSidepanelContentFormTypes) {
  const [isChangedTab, setIsChangedTab] = useState<boolean>(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (
      !isChangedTab &&
      activeTab !== InternalEnumTabs.DOCUMENTS &&
      activeTab !== InternalEnumTabs.GL_TRANSACTIONS
    ) {
      setIsChangedTab(true);

      if (warningsInfo && warningsInfo.entitySummary.unprocessed > 0) {
        setActiveTab(InternalEnumTabs.WARNING);
      } else {
        setActiveTab(InternalEnumTabs.GENERAL);
      }
    }
  }, [warningsInfo, isChangedTab]);

  useEffect(() => {
    if (duplicate) {
      setActiveTab(InternalEnumTabs.GENERAL);
    }
  }, [duplicate]);

  useEffect(() => {
    if (activeTab === InternalEnumTabs.DOCUMENTS && getTransactionDocuments) {
      getTransactionDocuments();
    }

    if (
      activeTab === InternalEnumTabs.GL_TRANSACTIONS &&
      getGeneralLedgerTransactionValues
    ) {
      getGeneralLedgerTransactionValues();
    }

    if (
      (activeTab === InternalEnumTabs.CHATS ||
        activeTab === InternalEnumTabs.WARNING) &&
      getTransactionsChats &&
      activePermission?.includes(PermissionEnum.CHANNELS_LIST)
    ) {
      getTransactionsChats();
    }

    if (
      activeTab === InternalEnumTabs.SIGNATURES &&
      getSignaturesTransactionById
    ) {
      getSignaturesTransactionById();
    }

    if (activeTab === InternalEnumTabs.AML && getInternalTransactionByIdAml) {
      getInternalTransactionByIdAml();
    }

    if (
      activeTab === InternalEnumTabs.LOG &&
      getLogsTransactionById &&
      activePermission?.includes(
        PermissionEnum.TRANSACTION_QUEUE_ITEMS_INTERNAL_LIST,
      )
    ) {
      getLogsTransactionById();
    }

    if (
      activeTab === InternalEnumTabs.CHANGE_LOG &&
      getTransactionChangeLogs &&
      activePermission?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getTransactionChangeLogs();
    }

    if (
      activeTab === InternalEnumTabs.COMMENTS &&
      getTransactionCommentsAction &&
      activePermission?.includes(
        PermissionEnum.TRANSACTIONS_INTERNAL_COMMENTS_LIST,
      )
    ) {
      getTransactionCommentsAction();
    }
  }, [activeTab, activePermission, transactionInfo]);

  return (
    <>
      {baseellaStatus === BaseellaStatus.NOT_SIGNED && canSignTransaction && (
        <div className={styles.notSignedAlert}>
          <div className={styles.notSignedText}>
            <IconAttention width={24} height={24} />
            Additional signatures required
          </div>
          <div className={styles.notSignedControls}>
            {!createdByOrganizationId && (
              <ButtonPrimary
                text={sendCodeInProgress ? 'Loading...' : 'Sign'}
                onClick={sendCode}
                className={styles.signButton}
                disabled={sendCodeInProgress}
              />
            )}
            <ButtonSecondary
              text="Reject"
              onClick={() =>
                setRejectPopupVisible && setRejectPopupVisible(true)
              }
            />
          </div>
        </div>
      )}
      {statusesInfo && !transactionInfo?.deletedAt && (
        <TransactionStatusChange
          transaction={statusesInfo}
          onNextStatus={onNextStatus}
          baseellaStatus={baseellaStatus}
          updateStatusIsLoading={updateStatusIsLoading}
          isLoadingStatuses={isLoadingStatuses}
          latestQueuedStatus={latestQueuedStatus}
          activeTab={activeTab as InternalEnumTabs}
        />
      )}
      {transactionInfo?.deletedAt && (
        <div className={styles.deleted}>{`Transaction deleted ${formatDate(
          transactionInfo?.deletedAt,
          true,
        )}`}</div>
      )}
      {transactionInfo && (
        <Tabs
          tabs={tabs}
          onClick={(value) => {
            setActiveTab(value as InternalEnumTabs);

            if (
              (parentScroll as number) >=
              Number(scrollRef.current?.offsetTop) - 130
            ) {
              parentRef?.current?.scrollTo({
                top: Number(scrollRef.current?.offsetTop) - 130,
              });
            }
          }}
          activeTab={activeTab}
          hideWarningTabs={!warningsInfo?.entitySummary.total}
          itemClassName={styles.transactionTab}
          warningsCount={warningsInfo?.entitySummary?.unprocessed}
          moreButtonClassName={styles.transactionMoreButton}
          isTransactionsTabs
          withShadow={
            (parentScroll as number) >
            // eslint-disable-next-line react-hooks/refs
            Number(scrollRef.current?.offsetTop) - 130
          }
          withSticky
        />
      )}
      <div
        ref={scrollRef}
        className={`${
          activeTab === InternalEnumTabs.GL_TRANSACTIONS
            ? styles.sidepanelContentWrapper
            : ''
        } ${styles.siddepanelContentFlex}`}
      >
        <div
          className={
            activeTab === InternalEnumTabs.GENERAL
              ? styles.activeTabContent
              : styles.tabContent
          }
        >
          <InternalTransactionGeneral
            general={transactionInfo}
            control={control}
            isEditable={isEditable}
            errors={errors}
            senderAccounts={senderAccounts}
            recipientAccounts={recipientAccounts}
            senderAccountsIsLoading={senderAccountsIsLoading}
            recipientAccountsIsLoading={recipientAccountsIsLoading}
            onSearchAutocomplete={onSearchAutocomplete}
            fields={fields}
            senderAccountBalance={senderAccountBalance}
            recipientAccountBalance={recipientAccountBalance}
            currencyId={currencyId}
            senderAccountBalances={senderAccountBalances}
            recipientAccountBalances={recipientAccountBalances}
            senderAccountBalancesIsLoading={senderAccountBalancesIsLoading}
            recipientAccountBalancesIsLoading={
              recipientAccountBalancesIsLoading
            }
            overdraftEnabled={overdraftEnabled}
            recipientAccountsWithInfo={recipientAccountsWithInfo}
            senderAccountsWithInfo={senderAccountsWithInfo}
            availableCurrencies={availableCurrencies}
            amount={amount}
            setAmount={setAmount}
            allCurrenciesList={allCurrenciesList}
            currenciesList={currenciesList}
            feeText={feeText}
            updateSenderBalance={updateSenderBalance}
            updateRecipientBalance={updateRecipientBalance}
            transferDate={transferDate}
            clearErrors={clearErrors}
            withTime={withTime}
            setWithTime={setWithTime}
          />
        </div>
        {!isCreateForm && (
          <div
            className={
              activeTab === InternalEnumTabs.WARNING
                ? styles.activeTabContent
                : styles.tabContent
            }
          >
            <TransactionWarnings
              warnings={warnings}
              employee={employee}
              onApplyTransactionAction={onApplyTransactionAction}
              transactionsTypes={transactionsTypes}
              setBlacklistDataVisible={setBlacklistDataVisible}
              setBlacklistData={setBlacklistData}
              setBlacklistItemVisible={setBlacklistItemVisible}
              setBlacklistDataItem={setBlacklistDataItem}
              applyTransactionsWarningIsLoading={
                applyTransactionsWarningIsLoading
              }
              recipients={recipientCompanyOfficials}
              senders={senderCompanyOfficials}
              recipientAccount={transactionInfo?.recipientAccount}
              senderAccount={transactionInfo?.senderAccount}
              chats={chats}
              createChatWithMessage={createChatWithMessage}
              chatCreateInProgress={chatCreateInProgress}
              activePermissions={activePermission}
              onResetTransactionWarningAction={onResetTransactionWarningAction}
              resetTransactionsWarningIsLoading={
                resetTransactionsWarningIsLoading
              }
              resetTransactionsWarningIsSuccess={
                resetTransactionsWarningIsSuccess
              }
              hiddenApprove={
                !activePermission?.includes(
                  PermissionEnum.TRANSACTIONS_INTERNAL_AML_APPROVE_ACTION,
                )
              }
              hiddenReject={
                !activePermission?.includes(
                  PermissionEnum.TRANSACTIONS_INTERNAL_AML_REJECT_ACTION,
                )
              }
              getWarningById={getInternalTransactionWarningById}
              isWarningByIdFetching={isWarningByIdFetching}
              isWarningByIdLoading={isWarningByIdLoading}
              warningsInfo={warningsInfo}
              selectedWarningId={selectedWarningId}
              warningActionError={warningActionError}
              isInternal
            />
          </div>
        )}
        {!isCreateForm && (
          <>
            <div
              className={
                activeTab === InternalEnumTabs.AML
                  ? styles.activeTabContent
                  : styles.tabContent
              }
            >
              <IncomingOutgoingAmlContent
                transactionType={TransactionTypeEnum.INTERNAL}
                isLoading={isLoadingAml as boolean}
                aml={amlInfo}
              />
            </div>
            <div
              className={
                activeTab === InternalEnumTabs.LOG
                  ? styles.activeTabContent
                  : styles.tabContent
              }
            >
              <TransactionLogsContent
                isLoading={isLoadingLogs as boolean}
                logs={logs}
                activePermission={activePermission?.includes(
                  PermissionEnum.TRANSACTION_QUEUE_ITEMS_INTERNAL_LIST,
                )}
              />
            </div>
            <div
              className={
                activeTab === InternalEnumTabs.CHANGE_LOG
                  ? styles.activeTabContent
                  : styles.tabContent
              }
            >
              <ChangeLogs
                entityType="TRANSACTION"
                entityId={transactionId as string}
                changeLogsIsLoading={changeLogsIsLoading}
                changeLogs={changeLogs}
                activePermission={activePermission}
                users={users}
                pagination={transactionChangeLogsPagination}
                changeLogsIsFetching={changeLogsIsFetching}
                getChangeLogs={getTransactionChangeLogs}
              />
            </div>
            <div
              className={
                activeTab === InternalEnumTabs.DOCUMENTS
                  ? styles.activeTabContent
                  : styles.tabContent
              }
            >
              <TransactionDocuments
                documents={documents}
                setSubPanelVisible={setSubPanelVisible}
                setSelectedDocumentName={setSelectedDocumentName}
                setTransactionSidepanelVisible={setTransactionSidepanelVisible}
                onUploadDocument={onUploadDocument}
                uploadDocumentInProgress={uploadDocumentInProgress}
                uploadDocumentIsSuccess={uploadDocumentIsSuccess}
                uploadDocumentErrors={uploadDocumentErrors}
                selectedDocumentId={selectedDocumentId}
                setSelectedDocumentId={setSelectedDocumentId}
                downloadInProgress={downloadInProgress}
                senderAccount={transactionInfo?.senderAccount}
                recipientAccount={transactionInfo?.recipientAccount}
                onDownloadDocument={onDownloadDocument}
                activePermission={activePermission}
                getTransactionDocument={getTransactionDocument}
                documentsIsLoading={documentsIsLoading}
                documentsPagination={documentsPagination}
                getTransactionDocuments={getTransactionDocuments}
                documentsIsFetching={documentsIsFetching}
                attachedDocumentsErrors={attachedDocumentsErrors}
                uploadedDocumentsErrors={uploadedDocumentsErrors}
                internal
              />
            </div>
            <div
              className={
                activeTab === InternalEnumTabs.SIGNATURES
                  ? styles.activeTabContent
                  : styles.tabContent
              }
            >
              <TransactionSignatures
                signatures={signatures}
                isLoadingSignatures={isLoadingSignatures}
              />
            </div>
            <div
              className={
                activeTab === InternalEnumTabs.GL_TRANSACTIONS
                  ? styles.activeTabContent
                  : styles.tabContent
              }
            >
              <TransactionAccountingEntries
                glTransactionsIsLoading={glTransactionsIsLoading}
                glTransactions={glTransactions}
                setGlTransactionSidePanelVisible={
                  setGlTransactionSidePanelVisible
                }
                onGlTransactionClick={onGlTransactionClick}
                setTransactionSidepanelVisible={setTransactionSidepanelVisible}
                currenciesList={currenciesList}
              />
            </div>
            <div
              className={
                activeTab === InternalEnumTabs.CHATS
                  ? styles.activeTabContent
                  : styles.tabContent
              }
            >
              <TransactionChats
                chats={chats}
                individualCustomer={transactionInfo?.senderAccount}
                onCreateTransactionChat={onCreateTransactionChat}
                chatCreateInProgress={chatCreateInProgress}
                currentEmployee={currentEmployee}
                recipients={recipientCompanyOfficials}
                senders={senderCompanyOfficials}
                recipientAccount={transactionInfo?.recipientAccount}
                senderAccount={transactionInfo?.senderAccount}
                activePermissions={activePermission}
                transactionChatsIsLoading={transactionChatsIsLoading}
                internal
              />
            </div>
            <div
              className={
                activeTab === InternalEnumTabs.COMMENTS
                  ? styles.activeTabContent
                  : styles.tabContent
              }
            >
              <TransactionComments
                comments={transactionComments}
                pagination={transactionCommentsPagination}
                isLoading={commentsIsLoading}
                isFetching={commentsIsFetching}
                commentsAvailable={activePermission?.includes(
                  PermissionEnum.TRANSACTIONS_INTERNAL_COMMENTS_LIST,
                )}
                getCommentsList={getTransactionCommentsAction}
                createAvailable={activePermission?.includes(
                  PermissionEnum.TRANSACTIONS_INTERNAL_COMMENTS_CREATE_ACTION,
                )}
                editAvailable={activePermission?.includes(
                  PermissionEnum.TRANSACTIONS_INTERNAL_COMMENTS_EDIT_ACTION,
                )}
                deleteAvailable={activePermission?.includes(
                  PermissionEnum.TRANSACTIONS_INTERNAL_COMMENTS_DELETE_ACTION,
                )}
                onAddCommentToTransaction={onAddCommentToTransaction}
                addCommentInProgress={addCommentInProgress}
                addCommentIsSuccess={addCommentIsSuccess}
                onEditCommentInTransaction={onEditCommentInTransaction}
                editTransactionCommentInProgress={
                  editTransactionCommentInProgress
                }
                editTrancastionCommentIsSuccess={
                  editTrancastionCommentIsSuccess
                }
                onDeleteCommentInTransaction={onDeleteCommentInTransaction}
                deleteCommentInTransactionInProgress={
                  deleteCommentInTransactionInProgress
                }
                deleteCommentInTranscationIsSuccess={
                  deleteCommentInTranscationIsSuccess
                }
                changeLogsAvailable={activePermission?.includes(
                  PermissionEnum.AUDIT_LOG_LIST,
                )}
                users={users}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
}
